import JSZip from 'jszip';
import type { Identifier, RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import { toApiIsoString } from './dateTimeUtils';

export const ADMIN_BLOG_RESOURCE = 'blogPages' as const;

export const ADMIN_BLOG_MAX_ASSET_SIZE_BYTES = 10 * 1024 * 1024;

export const ADMIN_BLOG_ALLOWED_ASSET_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'svg',
    'webp',
    'pdf',
    'txt',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
] as const;

export const ADMIN_BLOG_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'] as const;

export const ADMIN_BLOG_ASSET_ACCEPT = ADMIN_BLOG_ALLOWED_ASSET_EXTENSIONS.map((extension) => `.${extension}`).join(',');

export const ADMIN_BLOG_IMAGE_ACCEPT = ADMIN_BLOG_IMAGE_EXTENSIONS.map((extension) => `.${extension}`).join(',');

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

type AdminBlogListResponse = {
    items?: unknown[];
    data?: unknown[];
    total?: number;
    totalCount?: number;
    count?: number;
    page?: number;
    pageSize?: number;
};

export type AdminBlogRecord = RaRecord & {
    id: string;
    blogContentId: string;
    title: string;
    excerpt?: string;
    content: string;
    coverImageUrl?: string;
    coverImageUrlResolved?: string;
    isPublished: boolean;
    publishedAtUtc?: string | null;
    createdAt: string;
    updatedAt: string;
    isPublishedToTarget: boolean;
    status: 'Published' | 'Draft';
    tags: AdminBlogTagRecord[];
    tagIds: string[];
    [key: string]: unknown;
};

export type AdminBlogProductionStatusRecord = {
    blogContentId: string;
    targetConfigured: boolean;
    isPublishedToTarget: boolean;
    targetEnvironment: string;
    targetPublishedAtUtc?: string | null;
    targetUpdatedAtUtc?: string | null;
};

export type AdminBlogPublishAssetRecord = {
    sourceKey: string;
    publishedKey: string;
    sourceBucketName: string;
    publishedBucketName: string;
    copied: boolean;
};

export type AdminBlogPublishResultRecord = AdminBlogProductionStatusRecord & {
    isPublished: boolean;
    publishedAtUtc: string;
    sourceEnvironment: string;
    tagCount: number;
    tagTreeCount: number;
    assetCount: number;
    assets: AdminBlogPublishAssetRecord[];
};

export type AdminBlogTagRecord = RaRecord & {
    id: string;
    blogTagId: string;
    blogTagTreeId: string;
    tagTreeName: string;
    parentBlogTagId?: string;
    name: string;
    slug: string;
    sortOrder: number;
};

export type AdminBlogTagNodeRecord = RaRecord & {
    id: string;
    blogTagId: string;
    blogTagTreeId: string;
    parentBlogTagId?: string;
    name: string;
    slug: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    children: AdminBlogTagNodeRecord[];
};

export type AdminBlogTagTreeRecord = RaRecord & {
    id: string;
    blogTagTreeId: string;
    name: string;
    slug: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    tags: AdminBlogTagNodeRecord[];
};

export type AdminBlogTagOption = {
    blogTagId: string;
    blogTagTreeId: string;
    tagTreeName: string;
    parentBlogTagId?: string;
    name: string;
    slug: string;
    sortOrder: number;
    pathLabel: string;
};

export type AdminBlogAssetUpload = {
    fileUrl: string;
    fileName: string;
    contentType: string;
    extension: string;
    fileSize: number;
};

type BlogAssetValidationOptions = {
    imageOnly?: boolean;
};

type PaginationParams = {
    page?: number;
    perPage?: number;
};

type AdminBlogQueryParams = {
    id?: Identifier;
    ids?: Identifier[];
    data?: Record<string, unknown>;
    previousData?: Record<string, unknown>;
    filter?: Record<string, unknown>;
    pagination?: PaginationParams;
};

class AdminBlogRequestError extends Error {
    readonly status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'AdminBlogRequestError';
        this.status = status;
    }
}

type AdminBlogTagTreeCreateInput = {
    name: string;
    slug?: string;
    sortOrder?: number;
};

type AdminBlogTagTreeUpdateInput = AdminBlogTagTreeCreateInput & {
    tags?: AdminBlogTagNodeRecord[];
};

type AdminBlogTagCreateInput = {
    parentBlogTagId?: string;
    name: string;
    slug?: string;
    sortOrder?: number;
};

type AdminBlogTagUpdateInput = AdminBlogTagCreateInput;

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getAdminAssetBaseUrl(): string {
    const fileBaseUrl = trimTrailingSlash(String(import.meta.env.VITE_BASE_FILE_URL ?? ''));
    if (fileBaseUrl) {
        return fileBaseUrl;
    }

    const { baseUrl } = getAdminApiConfig();
    if (hasVersionedApiPath(baseUrl)) {
        return trimTrailingSlash(baseUrl.replace(/\/api\/v[^/]+$/i, ''));
    }

    return trimTrailingSlash(baseUrl);
}

function resolveAdminAssetUrl(value: unknown): string {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return '';

    if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
    }

    return new URL(rawValue.replace(/^\/+/, ''), `${getAdminAssetBaseUrl()}/`).toString();
}

export function resolveAdminBlogAssetUrl(value: unknown): string {
    return resolveAdminAssetUrl(value);
}

function getFileExtension(fileName: string): string {
    const trimmedName = fileName.trim().toLowerCase();
    const lastDotIndex = trimmedName.lastIndexOf('.');

    if (lastDotIndex < 0) {
        return '';
    }

    return trimmedName.slice(lastDotIndex + 1);
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    }

    if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }

    return `${bytes} bytes`;
}

export function getAdminBlogAssetRuleSummary(options: BlogAssetValidationOptions = {}): string {
    const allowedExtensions = options.imageOnly ? ADMIN_BLOG_IMAGE_EXTENSIONS : ADMIN_BLOG_ALLOWED_ASSET_EXTENSIONS;

    return `Max ${formatBytes(ADMIN_BLOG_MAX_ASSET_SIZE_BYTES)}. Allowed: ${allowedExtensions.join(', ')}.`;
}

export function validateAdminBlogAssetFile(file: File, options: BlogAssetValidationOptions = {}): string | null {
    if (!file) {
        return 'Please choose a file.';
    }

    if (file.size <= 0) {
        return 'The selected file is empty.';
    }

    if (file.size > ADMIN_BLOG_MAX_ASSET_SIZE_BYTES) {
        return `File size exceeds the ${formatBytes(ADMIN_BLOG_MAX_ASSET_SIZE_BYTES)} limit.`;
    }

    const extension = getFileExtension(file.name);
    const allowedExtensions: readonly string[] = options.imageOnly
        ? ADMIN_BLOG_IMAGE_EXTENSIONS
        : ADMIN_BLOG_ALLOWED_ASSET_EXTENSIONS;

    if (!extension || !allowedExtensions.includes(extension)) {
        return options.imageOnly
            ? `Cover image must use one of: ${ADMIN_BLOG_IMAGE_EXTENSIONS.join(', ')}.`
            : `File type must use one of: ${ADMIN_BLOG_ALLOWED_ASSET_EXTENSIONS.join(', ')}.`;
    }

    return null;
}

type ImportedBlogPackageAsset = {
    path: string;
    fileName: string;
    content: Uint8Array;
    mimeType: string;
};

type ImportedBlogPackage = {
    html: string;
    htmlFileName: string;
    assets: ImportedBlogPackageAsset[];
    title?: string;
    excerpt?: string;
};

type ImportedZipEntry = {
    dir?: boolean;
    name: string;
    async(type: 'arraybuffer' | 'text'): Promise<ArrayBuffer | string>;
};

function normalizeZipEntryPath(path: string): string {
    return path.replace(/\\/g, '/').replace(/^\.?\/+/, '').trim();
}

function hasUnsafeZipPath(path: string): boolean {
    return path.split('/').some((part) => part === '..');
}

function getZipEntryExtension(path: string): string {
    const normalizedPath = path.trim().toLowerCase();
    const dotIndex = normalizedPath.lastIndexOf('.');
    return dotIndex >= 0 ? normalizedPath.slice(dotIndex + 1) : '';
}

function guessMimeType(path: string): string {
    const extension = getZipEntryExtension(path);
    if (extension === 'css') return 'text/css';
    if (extension === 'js') return 'application/javascript';
    if (extension === 'svg') return 'image/svg+xml';
    if (extension === 'png') return 'image/png';
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension === 'gif') return 'image/gif';
    if (extension === 'webp') return 'image/webp';
    if (extension === 'ico') return 'image/x-icon';
    if (extension === 'html' || extension === 'htm') return 'text/html';
    return 'application/octet-stream';
}

async function readImportedBlogPackage(file: File): Promise<ImportedBlogPackage> {
    if (!file.name.toLowerCase().endsWith('.zip')) {
        throw new Error('Please choose a .zip file.');
    }

    const zip = await JSZip.loadAsync(file);
    const entries = Object.values(zip.files) as ImportedZipEntry[];

    const normalizedEntries = entries
        .filter((entry) => !entry.dir)
        .map((entry) => ({
            path: normalizeZipEntryPath(entry.name),
            entry,
        }))
        .filter(({ path }) => Boolean(path));

    if (normalizedEntries.length === 0) {
        throw new Error('The zip file is empty. Include index.html and an assets folder when assets are needed.');
    }

    const unsafeEntry = normalizedEntries.find(({ path }) => hasUnsafeZipPath(path));
    if (unsafeEntry) {
        throw new Error(`Unsafe zip path is not allowed: ${unsafeEntry.path}`);
    }

    const javascriptEntry = normalizedEntries.find(({ path }) => getZipEntryExtension(path) === 'js');
    if (javascriptEntry) {
        throw new Error(`JavaScript files are not allowed in blog page uploads: ${javascriptEntry.path}`);
    }

    const indexEntries = normalizedEntries
        .filter(({ path }) => path.toLowerCase().endsWith('index.html'))
        .sort((left, right) => left.path.split('/').length - right.path.split('/').length);
    const htmlEntry = indexEntries[0];
    if (!htmlEntry) {
        throw new Error('The zip file must include index.html.');
    }

    const basePath = htmlEntry.path.slice(0, -'index.html'.length);
    const scopedPath = (path: string) => path.slice(basePath.length);

    const assets = await Promise.all(
        normalizedEntries
            .filter(({ path }) => path.startsWith(`${basePath}assets/`))
            .map(async ({ path, entry }) => {
                const fileName = path.split('/').pop() ?? path;
                return {
                    path: scopedPath(path),
                    fileName,
                    content: new Uint8Array(await entry.async('arraybuffer') as ArrayBuffer),
                    mimeType: guessMimeType(path),
                } satisfies ImportedBlogPackageAsset;
            }),
    );

    const rawHtml = (await htmlEntry.entry.async('text')) as string;
    validateImportedHtml(rawHtml);

    return {
        html: rawHtml,
        htmlFileName: scopedPath(htmlEntry.path),
        assets,
        title: extractImportedTitle(rawHtml),
        excerpt: extractImportedExcerpt(rawHtml),
    };
}

function validateImportedHtml(html: string): void {
    if (/<script\b/i.test(html)) {
        throw new Error('Script tags are not allowed in uploaded blog HTML.');
    }

    if (/\son[a-z]+\s*=/i.test(html)) {
        throw new Error('Inline JavaScript event handlers are not allowed in uploaded blog HTML.');
    }

    if (/\bjavascript:/i.test(html)) {
        throw new Error('JavaScript URLs are not allowed in uploaded blog HTML.');
    }
}

function extractImportedTitle(html: string): string | undefined {
    const titleMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i) ?? html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    return normalizeImportedText(titleMatch?.[1]);
}

function extractImportedExcerpt(html: string): string | undefined {
    const descriptionMatch = html.match(/<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["'][^>]*>/i);
    if (descriptionMatch?.[1]) {
        return normalizeImportedText(descriptionMatch[1]);
    }

    const paragraphMatch = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    const paragraph = normalizeImportedText(paragraphMatch?.[1]);
    return paragraph && paragraph.length > 220 ? `${paragraph.slice(0, 217).trim()}...` : paragraph;
}

function normalizeImportedText(value: unknown): string | undefined {
    const text = String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return text || undefined;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function rewriteImportedBlogContent(html: string, uploadedAssets: Map<string, string>): string {
    let rewritten = html;

    for (const [path, url] of uploadedAssets.entries()) {
        const escapedPath = escapeRegExp(path);
        rewritten = rewritten.replace(new RegExp(`(["'(])${escapedPath}(["')])`, 'g'), `$1${url}$2`);

        const fileName = path.split('/').pop();
        if (fileName && fileName !== path) {
            const escapedFileName = escapeRegExp(fileName);
            rewritten = rewritten.replace(new RegExp(`(["'(])${escapedFileName}(["')])`, 'g'), `$1${url}$2`);
        }
    }

    return rewritten;
}

export async function importAdminBlogHtmlZip(file: File): Promise<{ content: string; title?: string; excerpt?: string }> {
    const packageData = await readImportedBlogPackage(file);
    const uploadedAssets = new Map<string, string>();

    for (const asset of packageData.assets) {
        const assetBuffer = asset.content.buffer.slice(
            asset.content.byteOffset,
            asset.content.byteOffset + asset.content.byteLength,
        ) as ArrayBuffer;
        const assetFile = new File([new Blob([assetBuffer], { type: asset.mimeType })], asset.fileName, { type: asset.mimeType });
        const validationMessage = validateAdminBlogAssetFile(assetFile);
        if (validationMessage) {
            throw new Error(`Asset ${asset.path}: ${validationMessage}`);
        }

        const uploadResult = await uploadAdminBlogAsset(assetFile);
        uploadedAssets.set(asset.path, resolveAdminBlogAssetUrl(uploadResult.fileUrl));
    }

    return {
        content: rewriteImportedBlogContent(packageData.html, uploadedAssets),
        title: packageData.title,
        excerpt: packageData.excerpt,
    };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }

    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin blog request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function getOptionalString(value: unknown): string | undefined {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? trimmed : undefined;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return undefined;
}

function getRequiredString(value: unknown): string {
    return getOptionalString(value) ?? '';
}

function getNumberValue(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return fallback;
}

function getBooleanValue(value: unknown, fallback = false): boolean {
    if (typeof value === 'boolean') return value;

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'published'].includes(normalized)) return true;
        if (['false', '0', 'no', 'draft'].includes(normalized)) return false;
    }

    return fallback;
}

function normalizeDateTimeValue(value: unknown): string | null {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return null;

    return toApiIsoString(rawValue);
}

function getListPayloadObject(payload: unknown): Record<string, unknown> | null {
    const unwrapped = unwrapAdminApiPayload(
        payload as Record<string, unknown> | AdminApiEnvelope<Record<string, unknown>>,
    );

    if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        return unwrapped as Record<string, unknown>;
    }

    return null;
}

function getListRecords(payload: unknown): unknown[] {
    const unwrapped = unwrapAdminApiPayload(payload as unknown[] | AdminApiEnvelope<unknown[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    if (unwrapped && typeof unwrapped === 'object') {
        const candidate = unwrapped as Record<string, unknown>;
        if (Array.isArray(candidate.items)) return candidate.items;
        if (Array.isArray(candidate.Items)) return candidate.Items as unknown[];
        if (Array.isArray(candidate.data)) return candidate.data;
    }

    return [];
}

function getListTotal(payload: unknown, fallback: number): number {
    const unwrapped = getListPayloadObject(payload);

    if (unwrapped) {
        if (typeof unwrapped.totalCount === 'number') return unwrapped.totalCount;
        if (typeof unwrapped.TotalCount === 'number') return unwrapped.TotalCount as number;
        if (typeof unwrapped.total === 'number') return unwrapped.total;
        if (typeof unwrapped.count === 'number') return unwrapped.count;
    }

    return fallback;
}

function normalizeTagIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(
        new Set(
            value
                .map((item) => getOptionalString(item))
                .filter((item): item is string => Boolean(item)),
        ),
    );
}

async function requestAdminBlog<T>(
    path = '',
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminBlog/${requestPath}` : `${apiRoot}/AdminBlog`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new AdminBlogRequestError(
            getErrorMessage(payload, `${response.status} ${response.statusText}`),
            response.status,
        );
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin blog request failed'));
        }
    }

    return payload as T;
}

function normalizeBlogRecord(item: unknown, fallbackId?: Identifier): AdminBlogRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const blogContentId = getOptionalString(record.blogContentId ?? record.id ?? fallbackId);

    if (!blogContentId) {
        throw new Error('Blog item is missing blogContentId');
    }

    const coverImageUrl = getOptionalString(record.coverImageUrl);
    const isPublished = getBooleanValue(record.isPublished);
    const tags = Array.isArray(record.tags) ? record.tags.map((tag) => normalizeBlogTag(tag)) : [];

    return {
        ...record,
        id: blogContentId,
        blogContentId,
        title: getRequiredString(record.title),
        excerpt: getOptionalString(record.excerpt),
        content: getRequiredString(record.content),
        coverImageUrl,
        coverImageUrlResolved: coverImageUrl ? resolveAdminAssetUrl(coverImageUrl) : undefined,
        isPublished,
        publishedAtUtc: getOptionalString(record.publishedAtUtc) ?? null,
        createdAt: getRequiredString(record.createdAt),
        updatedAt: getRequiredString(record.updatedAt),
        isPublishedToTarget: getBooleanValue(record.isPublishedToTarget),
        status: isPublished ? 'Published' : 'Draft',
        tags,
        tagIds: tags.map((tag) => tag.blogTagId),
    };
}

function normalizeBlogProductionStatus(item: unknown, fallbackId?: Identifier): AdminBlogProductionStatusRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const blogContentId = getOptionalString(record.blogContentId ?? record.id ?? fallbackId);

    if (!blogContentId) {
        throw new Error('Blog production status is missing blogContentId');
    }

    return {
        blogContentId,
        targetConfigured: getBooleanValue(record.targetConfigured),
        isPublishedToTarget: getBooleanValue(record.isPublishedToTarget),
        targetEnvironment: getOptionalString(record.targetEnvironment) ?? '',
        targetPublishedAtUtc: getOptionalString(record.targetPublishedAtUtc) ?? null,
        targetUpdatedAtUtc: getOptionalString(record.targetUpdatedAtUtc) ?? null,
    };
}

function normalizeBlogPublishAsset(item: unknown): AdminBlogPublishAssetRecord {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        sourceKey: getRequiredString(record.sourceKey),
        publishedKey: getRequiredString(record.publishedKey),
        sourceBucketName: getRequiredString(record.sourceBucketName),
        publishedBucketName: getRequiredString(record.publishedBucketName),
        copied: getBooleanValue(record.copied),
    };
}

function normalizeBlogPublishResult(item: unknown, fallbackId?: Identifier): AdminBlogPublishResultRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const status = normalizeBlogProductionStatus(
        {
            ...record,
            targetConfigured: true,
            isPublishedToTarget: true,
            targetPublishedAtUtc: record.publishedAtUtc ?? record.targetPublishedAtUtc,
        },
        fallbackId,
    );

    return {
        ...status,
        isPublished: getBooleanValue(record.isPublished),
        publishedAtUtc: getRequiredString(record.publishedAtUtc),
        sourceEnvironment: getOptionalString(record.sourceEnvironment) ?? '',
        tagCount: getNumberValue(record.tagCount),
        tagTreeCount: getNumberValue(record.tagTreeCount),
        assetCount: getNumberValue(record.assetCount),
        assets: Array.isArray(record.assets) ? record.assets.map((asset) => normalizeBlogPublishAsset(asset)) : [],
    };
}

function normalizeBlogTag(item: unknown): AdminBlogTagRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const blogTagId = getOptionalString(record.blogTagId ?? record.id);

    if (!blogTagId) {
        throw new Error('Blog tag item is missing blogTagId');
    }

    return {
        ...record,
        id: blogTagId,
        blogTagId,
        blogTagTreeId: getRequiredString(record.blogTagTreeId),
        tagTreeName: getRequiredString(record.tagTreeName),
        parentBlogTagId: getOptionalString(record.parentBlogTagId),
        name: getRequiredString(record.name),
        slug: getRequiredString(record.slug),
        sortOrder: getNumberValue(record.sortOrder),
    };
}

function normalizeBlogTagNode(item: unknown): AdminBlogTagNodeRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const blogTagId = getOptionalString(record.blogTagId ?? record.id);

    if (!blogTagId) {
        throw new Error('Blog tag node is missing blogTagId');
    }

    return {
        ...record,
        id: blogTagId,
        blogTagId,
        blogTagTreeId: getRequiredString(record.blogTagTreeId),
        parentBlogTagId: getOptionalString(record.parentBlogTagId),
        name: getRequiredString(record.name),
        slug: getRequiredString(record.slug),
        sortOrder: getNumberValue(record.sortOrder),
        createdAt: getRequiredString(record.createdAt),
        updatedAt: getRequiredString(record.updatedAt),
        children: Array.isArray(record.children) ? record.children.map((child) => normalizeBlogTagNode(child)) : [],
    };
}

function normalizeBlogTagTree(item: unknown): AdminBlogTagTreeRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const blogTagTreeId = getOptionalString(record.blogTagTreeId ?? record.id);

    if (!blogTagTreeId) {
        throw new Error('Blog tag tree item is missing blogTagTreeId');
    }

    return {
        ...record,
        id: blogTagTreeId,
        blogTagTreeId,
        name: getRequiredString(record.name),
        slug: getRequiredString(record.slug),
        sortOrder: getNumberValue(record.sortOrder),
        createdAt: getRequiredString(record.createdAt),
        updatedAt: getRequiredString(record.updatedAt),
        tags: Array.isArray(record.tags) ? record.tags.map((tag) => normalizeBlogTagNode(tag)) : [],
    };
}

function sortBlogTagTrees(trees: AdminBlogTagTreeRecord[]): AdminBlogTagTreeRecord[] {
    return [...trees].sort((left, right) => {
        const sortOrderDiff = left.sortOrder - right.sortOrder;
        if (sortOrderDiff !== 0) {
            return sortOrderDiff;
        }

        return left.name.localeCompare(right.name);
    });
}

function buildTagTreeNodeUpsertPayload(tags: AdminBlogTagNodeRecord[]): Array<Record<string, unknown>> {
    return tags.map((tag) => ({
        blogTagId: tag.blogTagId,
        name: tag.name,
        slug: getOptionalString(tag.slug) ?? null,
        sortOrder: tag.sortOrder,
        children: buildTagTreeNodeUpsertPayload(tag.children),
    }));
}

export function flattenAdminBlogTagTrees(trees: AdminBlogTagTreeRecord[]): AdminBlogTagOption[] {
    const options: AdminBlogTagOption[] = [];

    const visit = (nodes: AdminBlogTagNodeRecord[], tree: AdminBlogTagTreeRecord, ancestors: string[]) => {
        const sortedNodes = [...nodes].sort((left, right) => {
            const sortOrderDiff = left.sortOrder - right.sortOrder;
            if (sortOrderDiff !== 0) return sortOrderDiff;
            return left.name.localeCompare(right.name);
        });

        for (const node of sortedNodes) {
            const path = [...ancestors, node.name];
            options.push({
                blogTagId: node.blogTagId,
                blogTagTreeId: node.blogTagTreeId,
                tagTreeName: tree.name,
                parentBlogTagId: node.parentBlogTagId,
                name: node.name,
                slug: node.slug,
                sortOrder: node.sortOrder,
                pathLabel: `${tree.name} / ${path.join(' / ')}`,
            });
            visit(node.children, tree, path);
        }
    };

    for (const tree of sortBlogTagTrees(trees)) {
        visit(tree.tags, tree, []);
    }

    return options;
}

function toQueryValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

function toPublishedQueryValue(value: unknown): string {
    if (typeof value === 'boolean') {
        return String(value);
    }

    const normalized = String(value ?? '').trim().toLowerCase();
    if (['published', 'true', '1', 'yes'].includes(normalized)) return 'true';
    if (['draft', 'false', '0', 'no'].includes(normalized)) return 'false';
    return '';
}

function buildAdminBlogWritePayload(data: Record<string, unknown>) {
    const isPublished = getBooleanValue(data.isPublished);

    return {
        title: toQueryValue(data.title),
        excerpt: getOptionalString(data.excerpt) ?? null,
        content: toQueryValue(data.content),
        coverImageUrl: getOptionalString(data.coverImageUrl) ?? null,
        isPublished,
        publishedAtUtc: isPublished ? normalizeDateTimeValue(data.publishedAtUtc) : null,
        tagIds: normalizeTagIds(data.tagIds),
    };
}

function getAdminBlogContentIdFromParams(params: AdminBlogQueryParams): Identifier {
    const id =
        getOptionalString(params.previousData?.blogContentId) ??
        getOptionalString(params.data?.blogContentId) ??
        getOptionalString(params.id);

    if (!id) {
        throw new Error('Blog item is missing blogContentId');
    }

    return id;
}

function buildAdminBlogTagTreePayload(data: AdminBlogTagTreeCreateInput | AdminBlogTagTreeUpdateInput) {
    const requestBody: Record<string, unknown> = {
        name: toQueryValue(data.name),
        slug: getOptionalString(data.slug) ?? null,
        sortOrder: getNumberValue(data.sortOrder),
    };

    if ('tags' in data && data.tags) {
        requestBody.tags = buildTagTreeNodeUpsertPayload(data.tags);
    }

    return requestBody;
}

function buildAdminBlogTagPayload(data: AdminBlogTagCreateInput | AdminBlogTagUpdateInput) {
    return {
        parentBlogTagId: getOptionalString(data.parentBlogTagId) ?? null,
        name: toQueryValue(data.name),
        slug: getOptionalString(data.slug) ?? null,
        sortOrder: getNumberValue(data.sortOrder),
    };
}

export function isAdminBlogResource(resource: string): resource is typeof ADMIN_BLOG_RESOURCE {
    return resource === ADMIN_BLOG_RESOURCE;
}

export async function getAdminBlogList(params: AdminBlogQueryParams) {
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const { page = 1, perPage = 10 } = params.pagination ?? {};

    const query = {
        Page: String(page),
        PageSize: String(perPage),
        Search: toQueryValue(filter.search ?? filter.q ?? ''),
        DateFromUtc: normalizeDateTimeValue(filter.dateFromUtc) ?? '',
        DateToUtc: normalizeDateTimeValue(filter.dateToUtc) ?? '',
        IsPublished: toPublishedQueryValue(filter.publishedState ?? filter.isPublished),
    };

    const payload = await requestAdminBlog<AdminBlogListResponse | AdminApiEnvelope<AdminBlogListResponse>>(
        '',
        { method: 'GET' },
        query,
    );
    const records = getListRecords(payload).map((item) => normalizeBlogRecord(item));

    return {
        data: records,
        total: getListTotal(payload, records.length),
    };
}

export async function getAdminBlogOne(params: AdminBlogQueryParams) {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'GET',
        }),
    );

    return { data: normalizeBlogRecord(payload, params.id) };
}

export async function getManyAdminBlog(params: AdminBlogQueryParams) {
    const data = await Promise.all(
        (params.ids ?? []).map(async (id: Identifier) => {
            const payload = unwrapAdminApiPayload(
                await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(String(id), {
                    method: 'GET',
                }),
            );

            return normalizeBlogRecord(payload, id);
        }),
    );

    return { data };
}

export async function createAdminBlog(params: AdminBlogQueryParams) {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>('', {
            method: 'POST',
            body: JSON.stringify(buildAdminBlogWritePayload((params.data ?? {}) as Record<string, unknown>)),
        }),
    );

    return { data: normalizeBlogRecord(payload) };
}

export async function updateAdminBlog(params: AdminBlogQueryParams) {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'PUT',
            body: JSON.stringify(buildAdminBlogWritePayload((params.data ?? {}) as Record<string, unknown>)),
        }),
    );

    return { data: normalizeBlogRecord(payload, params.id) };
}

export async function deleteAdminBlog(params: AdminBlogQueryParams) {
    const existing = params.previousData
        ? normalizeBlogRecord(params.previousData, params.id)
        : (await getAdminBlogOne(params)).data;
    const blogContentId = getAdminBlogContentIdFromParams({
        ...params,
        previousData: existing,
    });

    try {
        await requestAdminBlog(String(blogContentId), {
            method: 'DELETE',
        });
    } catch (error) {
        if (!(error instanceof AdminBlogRequestError) || ![404, 405].includes(error.status)) {
            throw error;
        }

        await requestAdminBlog(`delete/${String(blogContentId)}`, {
            method: 'DELETE',
        });
    }

    return { data: existing };
}

export async function getAdminBlogProductionStatus(blogContentId: Identifier): Promise<AdminBlogProductionStatusRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`publish-target/status/${String(blogContentId)}`, {
            method: 'GET',
        }),
    );

    return normalizeBlogProductionStatus(payload, blogContentId);
}

export async function publishAdminBlogToProduction(blogContentId: Identifier): Promise<AdminBlogPublishResultRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`publish/${String(blogContentId)}`, {
            method: 'POST',
        }),
    );

    return normalizeBlogPublishResult(payload, blogContentId);
}

export async function uploadAdminBlogAsset(file: File): Promise<AdminBlogAssetUpload> {
    const formData = new FormData();
    formData.append('File', file);

    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>('upload', {
            method: 'POST',
            body: formData,
        }),
    );

    const record = (payload ?? {}) as Record<string, unknown>;

    return {
        fileUrl: getRequiredString(record.fileUrl ?? record.FileUrl),
        fileName: getRequiredString(record.fileName ?? record.FileName) || file.name,
        contentType: getRequiredString(record.contentType ?? record.ContentType) || file.type,
        extension: getRequiredString(record.extension ?? record.Extension),
        fileSize: Number(record.fileSize ?? record.FileSize ?? file.size),
    };
}

export async function getAdminBlogTagTrees(): Promise<AdminBlogTagTreeRecord[]> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>('tag-trees', {
            method: 'GET',
        }),
    );

    const items = Array.isArray(payload) ? payload : [];
    return sortBlogTagTrees(items.map((item) => normalizeBlogTagTree(item)));
}

export async function getAdminBlogTagTree(blogTagTreeId: Identifier): Promise<AdminBlogTagTreeRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`tag-trees/${String(blogTagTreeId)}`, {
            method: 'GET',
        }),
    );

    return normalizeBlogTagTree(payload);
}

export async function createAdminBlogTagTree(data: AdminBlogTagTreeCreateInput): Promise<AdminBlogTagTreeRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>('tag-trees', {
            method: 'POST',
            body: JSON.stringify(buildAdminBlogTagTreePayload(data)),
        }),
    );

    return normalizeBlogTagTree(payload);
}

export async function updateAdminBlogTagTree(
    blogTagTreeId: Identifier,
    data: AdminBlogTagTreeUpdateInput,
): Promise<AdminBlogTagTreeRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`tag-trees/${String(blogTagTreeId)}`, {
            method: 'PUT',
            body: JSON.stringify(buildAdminBlogTagTreePayload(data)),
        }),
    );

    return normalizeBlogTagTree(payload);
}

export async function deleteAdminBlogTagTree(blogTagTreeId: Identifier): Promise<void> {
    await requestAdminBlog(`tag-trees/${String(blogTagTreeId)}`, {
        method: 'DELETE',
    });
}

export async function createAdminBlogTag(
    blogTagTreeId: Identifier,
    data: AdminBlogTagCreateInput,
): Promise<AdminBlogTagNodeRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`tag-trees/${String(blogTagTreeId)}/tags`, {
            method: 'POST',
            body: JSON.stringify(buildAdminBlogTagPayload(data)),
        }),
    );

    return normalizeBlogTagNode(payload);
}

export async function updateAdminBlogTag(
    blogTagId: Identifier,
    data: AdminBlogTagUpdateInput,
): Promise<AdminBlogTagNodeRecord> {
    const payload = unwrapAdminApiPayload(
        await requestAdminBlog<unknown | AdminApiEnvelope<unknown>>(`tags/${String(blogTagId)}`, {
            method: 'PUT',
            body: JSON.stringify(buildAdminBlogTagPayload(data)),
        }),
    );

    return normalizeBlogTagNode(payload);
}

export async function deleteAdminBlogTag(blogTagId: Identifier): Promise<void> {
    await requestAdminBlog(`tags/${String(blogTagId)}`, {
        method: 'DELETE',
    });
}
